import React, { useState, useEffect, useCallback } from 'react';
import { Container, Box } from '@mui/material';
import { NoProjectsEmptyState } from '../components/dashboard/EmptyState';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE from '../apiBase';
import { RefreshButton, PRListFilters, PRListTable, PRListPagination, PRListStats } from '../components/pullRequestList';

const columns = [
  { id: 'checkbox', label: '', sortable: false },
  { id: 'repository', label: 'Repository', sortable: true },
  { id: 'pr_number', label: 'PR #', sortable: true },
  { id: 'title', label: 'Title', sortable: true },
  { id: 'pr_author', label: 'Author', sortable: true },
  { id: 'severity', label: 'Severity', sortable: true },
  { id: 'issues', label: 'Issues', sortable: true },
  { id: 'fixed_issues', label: 'Fixed Issues', sortable: true },
  { id: 'pr_created_at', label: 'Created', sortable: true },
  { id: 'reviewed_at', label: 'Reviewed', sortable: true },
  { id: 'jira_accuracy_avg', label: 'JIRA Accuracy', sortable: true },
  { id: 'status', label: 'Status', sortable: true },
  { id: 'actions', label: 'Actions', sortable: false },
];


function PullRequestList() {
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    projectKey: '',
    search: '',
    status: 'all',
    severity: 'all',
  });
  const [sort, setSort] = useState({ field: 'reviewed_at', direction: 'desc' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({
    totalPRs: 0,
    criticalIssues: 0,
    avgFixRate: 0,
    underReview: 0
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const navigate = useNavigate();

  const fetchStats = useCallback(async () => {
    if (!filters.projectKey) return;

    try {
      const { data } = await axios.get(`${API_BASE}/api/dashboard`, {
        params: {
          project_key: filters.projectKey,
          range: '30d'
        }
      });

      const dashboardStats = data.stats || {};
      const issueTrends = data.issueTrends || { critical: [], medium: [], low: [] };

      // Calculate totals from issue trends
      const criticalCount = (issueTrends.critical || []).reduce((sum, item) => sum + item.count, 0);
      const mediumCount = (issueTrends.medium || []).reduce((sum, item) => sum + item.count, 0);
      const lowCount = (issueTrends.low || []).reduce((sum, item) => sum + item.count, 0);
      const totalIssues = criticalCount + mediumCount + lowCount;

      // Calculate average JIRA accuracy
      const jiraAccuracy = data.jiraAccuracy || [];
      const avgJiraAccuracy = jiraAccuracy.length > 0
        ? jiraAccuracy.reduce((sum, item) => sum + item.accuracy, 0) / jiraAccuracy.length
        : 0;

      // Calculate average fix rate (mock calculation - you can replace with real data)
      const avgFixRate = totalIssues > 0 ? Math.floor((totalIssues - criticalCount) / totalIssues * 100) : 0;

      setStats((prev) => ({
        ...prev,
        totalPRs: dashboardStats.totalPRs,
        criticalIssues: criticalCount,
        underReview: dashboardStats.underReview,
      }));
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, [filters.projectKey]);

  const fetchPRs = useCallback(async () => {
    // If no project is selected, do not fetch and clear list
    if (!filters.projectKey) {
      setReviews([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // Determine provider for current selected project (if available)
      const selectedProject = projects.find((p) => {
        const proj = p.project || p;
        return proj && proj.api_key === (filters.projectKey || '');
      });
      const repositoryType = ((selectedProject && (selectedProject.project || selectedProject).repository_type) || '').toLowerCase();

      const params = {
        project_key: filters.projectKey,
        search: filters.search,
        sort: repositoryType === 'bitbucket' ? 'pr_number' : sort.field,
        order: repositoryType === 'bitbucket' ? 'desc' : sort.direction,
        page: page + 1,
        limit: rowsPerPage,
        ...(filters.status !== 'all' && { status: filters.status }),
        ...(filters.severity !== 'all' && { severity: filters.severity }),
      };
      const { data } = await axios.get(`${API_BASE}/api/code-reviews`, { params });
      const incoming = Array.isArray(data.reviews) ? data.reviews : [];
      const reviewsSorted = repositoryType === 'bitbucket'
        ? [...incoming].sort((a, b) => {
          const an = typeof a.pr_number === 'string' ? parseInt(a.pr_number, 10) : a.pr_number;
          const bn = typeof b.pr_number === 'string' ? parseInt(b.pr_number, 10) : b.pr_number;
          const aNum = Number.isFinite(an) ? an : -Infinity;
          const bNum = Number.isFinite(bn) ? bn : -Infinity;
          return bNum - aNum; // latest PR number first
        })
        : incoming;
      setReviews(reviewsSorted);
      setTotal(data.total || 0);
    } catch (error) {
      console.error("Failed to fetch PRs:", error);
      setReviews([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters, sort, page, rowsPerPage, projects]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/api/projects`);
        setProjects(data);
        if (data.length > 0) {
          handleFilterChange('projectKey', data[0].api_key);
        }
      } catch (err) {
        setError('Failed to fetch projects.');
        console.error(err);
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    fetchPRs();
  }, [fetchPRs]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Dynamically compute Avg Fix Rate from actual fixed/intentional issues in loaded PRs
  useEffect(() => {
    if (!Array.isArray(reviews) || reviews.length === 0) {
      setStats((prev) => ({ ...prev, avgFixRate: 0 }));
      return
    }

    let sumTotalIssues = 0
    let sumResolvedIssues = 0

    for (const pr of reviews) {
      const total = (pr.critical_code_issues?.length || 0) + (pr.medium_code_issues?.length || 0) + (pr.low_code_issues?.length || 0)
      const fixedArr = Array.isArray(pr.fixed_code_issues) ? pr.fixed_code_issues : []
      const intentionalArr = Array.isArray(pr.marked_intentional_issues) ? pr.marked_intentional_issues : []
      const resolvedRaw = fixedArr.length + intentionalArr.length
      const resolved = total > 0 ? Math.min(resolvedRaw, total) : 0

      sumTotalIssues += total
      sumResolvedIssues += resolved
    }

    const rate = sumTotalIssues > 0 ? Math.round((sumResolvedIssues / sumTotalIssues) * 100) : 0
    setStats((prev) => ({ ...prev, avgFixRate: rate }))
  }, [reviews])

  useEffect(() => {
    const handler = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchTerm }));
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const handleFilterChange = (name, value) => {
    if (name === 'search') {
      setSearchTerm(value);
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
    }
    setPage(0);
  };

  const handleSort = (field) => {
    if (columns.find(c => c.id === field)?.sortable) {
      setSort((prev) => ({
        field,
        direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
      }));
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getIssuesCount = (pr) =>
    (pr.critical_code_issues?.length || 0) +
    (pr.medium_code_issues?.length || 0) +
    (pr.low_code_issues?.length || 0);

  const getSeverity = (pr) => {
    if (pr.critical_code_issues?.length > 0) return 'Critical';
    if (pr.medium_code_issues?.length > 0) return 'Medium';
    if (pr.low_code_issues?.length > 0) return 'Low';
    return 'None';
  };

  const getFixedIssuesData = (pr) => {
    const critical = pr.critical_code_issues?.length || 0;
    const medium = pr.medium_code_issues?.length || 0;
    const low = pr.low_code_issues?.length || 0;
    const total = critical + medium + low;

    const fixedArr = Array.isArray(pr.fixed_code_issues) ? pr.fixed_code_issues : [];
    const intentionalArr = Array.isArray(pr.marked_intentional_issues) ? pr.marked_intentional_issues : [];
    const combined = [...fixedArr, ...intentionalArr];

    const toSeverity = (s) => (typeof s === 'string' ? s.toLowerCase() : '');
    const fixedCritical = combined.filter(it => toSeverity(it.severity) === 'critical').length;
    const fixedMedium = combined.filter(it => toSeverity(it.severity) === 'medium').length;
    const fixedLow = combined.filter(it => toSeverity(it.severity) === 'low').length;
    const totalFixedRaw = fixedCritical + fixedMedium + fixedLow;
    const totalFixed = total > 0 ? Math.min(totalFixedRaw, total) : 0;

    return {
      total,
      totalFixed,
      critical,
      medium,
      low,
      fixedCritical,
      fixedMedium,
      fixedLow,
      percentage: total > 0 ? Math.round((totalFixed / total) * 100) : 0
    };
  };

  const getStatus = (pr) => {
    // Mock status based on PR data (you can replace with real data)
    if (pr.state === 'closed') return 'Closed';
    if (pr.state === 'merged') return 'Merged';
    if (pr.draft) return 'Draft';
    return 'Open';
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRows(reviews.map(pr => pr.unique_id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (prId) => {
    setSelectedRows(prev =>
      prev.includes(prId)
        ? prev.filter(id => id !== prId)
        : [...prev, prId]
    );
  };

  const handleRowClick = (pr) => {
    const platform = (pr.platform || '').toLowerCase();
    const repoStr = pr.repository || '';
    const parts = repoStr.split('/').filter(Boolean);
    const projectKeyParam = `?project_key=${encodeURIComponent(filters.projectKey || '')}`;
    // Fallback provider from selected project if platform is missing/empty
    const selectedProject = projects.find((p) => {
      const proj = p.project || p;
      return proj && proj.api_key === (filters.projectKey || '');
    });
    const provider = ((selectedProject && (selectedProject.project || selectedProject).repository_type) || '').toLowerCase();
    const source = platform || provider;
    // Route by source to the correct details page
    if (source === 'github') {
      if (parts.length >= 2) {
        const [owner, repo] = parts;
        navigate(`/app/pull-requests/github/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/${pr.pr_number}/details${projectKeyParam}`);
        return;
      }
      // Legacy fallback (ownerAndRepo)
      navigate(`/app/pull-requests/${encodeURIComponent(repoStr)}/${pr.pr_number}/details${projectKeyParam}`);
      return;
    }
    if (source === 'bitbucket') {
      if (parts.length >= 2) {
        const [workspace, repoSlug] = parts;
        navigate(`/app/pull-requests/bitbucket/${encodeURIComponent(workspace)}/${encodeURIComponent(repoSlug)}/${pr.pr_number}/details${projectKeyParam}`);
        return;
      }
      // Fallback to GitHub-like legacy if shape unexpected
      navigate(`/app/pull-requests/${encodeURIComponent(repoStr)}/${pr.pr_number}/details${projectKeyParam}`);
      return;
    }
    if (source === 'gitlab') {
      // For GitLab we pass the full project ID (group/repo) as a single param
      navigate(`/app/pull-requests/gitlab/${encodeURIComponent(repoStr)}/${pr.pr_number}/details${projectKeyParam}`);
      return;
    }
    if (source === 'azure_devops') {
      if (parts.length >= 3) {
        const [organization, project, repositoryID] = parts;
        navigate(`/app/pull-requests/${encodeURIComponent(organization)}/${encodeURIComponent(project)}/${encodeURIComponent(repositoryID)}/${pr.pr_number}/details${projectKeyParam}`);
        return;
      }
    }
    // Default: treat as Azure DevOps style if 3 segments, else legacy
    if (parts.length >= 3) {
      const [organization, project, repositoryID] = parts;
      navigate(`/app/pull-requests/${encodeURIComponent(organization)}/${encodeURIComponent(project)}/${encodeURIComponent(repositoryID)}/${pr.pr_number}/details${projectKeyParam}`);
      return;
    }
    // Final fallback to legacy
    navigate(`/app/pull-requests/${encodeURIComponent(repoStr)}/${pr.pr_number}/details${projectKeyParam}`);
  };

  return (
    <Box sx={{ bgcolor: "#fbfbfb", minHeight: "100vh", color: "#1f1f1f", p: { xs: 2, sm: 3, md: 4 }, }}>
      <Container sx={{ maxWidth: { lg: "1000px", xl: "1200px" } }}>
        {Array.isArray(projects) && projects.length === 0 ? (
          <NoProjectsEmptyState
            onAction={() => (window.location.href = '/projects')}
            actionText="Create Project"
          />
        ) : (
          <>
            <Box sx={{
              borderRadius: 1,
              p: '1rem',
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              mb: 2,
              display: 'block'
            }}>
              <PRListFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                projects={projects}
                searchTerm={searchTerm}
                onSearchChange={(e) => setSearchTerm(e.target.value)}
              />
            </Box>
            <PRListStats stats={stats} loading={loading} />
            <RefreshButton onRefresh={fetchPRs} loading={loading} />
            <PRListPagination
              total={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
            <PRListTable
              reviews={reviews}
              columns={columns}
              sort={sort}
              onSort={handleSort}
              onRowClick={handleRowClick}
              getIssuesCount={getIssuesCount}
              getSeverity={getSeverity}
              getFixedIssuesData={getFixedIssuesData}
              getStatus={getStatus}
              selectedRows={selectedRows}
              onSelectAll={handleSelectAll}
              onSelectRow={handleSelectRow}
              loading={loading}
            />
          </>
        )}
      </Container>
    </Box>
  );
}

export default PullRequestList; 